import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Clock, AlertTriangle, Mail } from "lucide-react";
import { sendNowEmail } from "@/services/routeService";
import { toAlertPreferencesDto } from "@/services/routeAlertService";

// Map backend frequency (ms) to UI key
const FREQUENCY_TO_KEY = {
  900000: "15min",
  1800000: "30min",
  3600000: "60min",
  86400000: "once",
};

// Parse "HH:MM to HH:MM" into { start: "HH:MM", end: "HH:MM" }
const parseTimeOfDayRange = (str) => {
  if (!str || typeof str !== "string") return null;
  const match = str.match(/^(\d{2}:\d{2}) to (\d{2}:\d{2})$/);
  if (!match) return null;
  return { start: match[1], end: match[2] };
};

// Map backend frequency ms to UI key for speed threshold
const mpsToMph = (mps) => Math.round(mps * 2.23694);

// Custom time selector component that's mobile-friendly
const TimeSelector = ({ value, onChange, label }) => {
  // Parse the time value (HH:MM format)
  const [hour, minute] = value.split(":").map(Number);

  // Generate hours (12-hour format)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);

  // Generate minutes (in 15-minute increments for easier selection)
  const minutes = [0, 15, 30, 45];

  // Determine if it's AM or PM
  const isPM = hour >= 12;
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  const handleTimeChange = (newHour, newMinute, newIsPM) => {
    // Convert to 24-hour format
    let hour24 = newHour;
    if (newHour === 12 && !newIsPM) {
      hour24 = 0; // 12 AM
    } else if (newHour !== 12 && newIsPM) {
      hour24 = newHour + 12;
    }

    // Format as HH:MM
    const formattedTime = `${hour24.toString().padStart(2, "0")}:${newMinute
      .toString()
      .padStart(2, "0")}`;
    onChange(formattedTime);
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-xs text-gray-500">{label}</label>}
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-gray-400" />

        {/* Hour selector */}
        <select
          value={displayHour}
          onChange={(e) =>
            handleTimeChange(Number(e.target.value), minute, isPM)
          }
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {hours.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>

        <span className="text-gray-500">:</span>

        {/* Minute selector */}
        <select
          value={minute}
          onChange={(e) =>
            handleTimeChange(displayHour, Number(e.target.value), isPM)
          }
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m.toString().padStart(2, "0")}
            </option>
          ))}
        </select>

        {/* AM/PM selector */}
        <select
          value={isPM ? "PM" : "AM"}
          onChange={(e) =>
            handleTimeChange(displayHour, minute, e.target.value === "PM")
          }
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

const RouteEmailConfig = ({
  isOpen,
  onClose,
  onSaveConfig,
  routeData,
  userData,
  loading,
  error,
  initialConfig,
  saveLabel,
  routeId,
}) => {
  // Time window settings
  const [startTime, setStartTime] = useState("07:00");
  const [endTime, setEndTime] = useState("09:00");

  // Days of week
  const [daysOfWeek, setDaysOfWeek] = useState({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  });

  // Email frequency settings
  const [emailFrequency, setEmailFrequency] = useState("once");

  // Reports to include
  const [reportsToInclude, setReportsToInclude] = useState({
    travelTime: true,
    congestion: true,
    construction: true,
    incidents: true,
    weather: true,
  });

  // Speed threshold with new range
  const [speedThreshold, setSpeedThreshold] = useState(35);
  const [disableSpeedThreshold, setDisableSpeedThreshold] = useState(false);

  // Severe incidents settings
  const [receiveAllSevere, setReceiveAllSevere] = useState(false);

  // Quiet hours for severe incidents
  const [enableQuietHours, setEnableQuietHours] = useState(false);
  const [quietHoursStart, setQuietHoursStart] = useState("22:00");
  const [quietHoursEnd, setQuietHoursEnd] = useState("06:00");

  // Send Now state
  const [sendingNow, setSendingNow] = useState(false);
  const [sendNowMessage, setSendNowMessage] = useState(null);
  const sendNowTimerRef = useRef(null);

  // On every open, reset ALL fields to defaults and apply the route's saved
  // config on top.  The dialog stays mounted between opens, so only
  // overwriting fields present in initialConfig leaked the previously
  // displayed route's values into other routes and new routes (gateway#10).
  useEffect(() => {
    if (!isOpen) return;
    const cfg = initialConfig || {};

    if (cfg.noAlerts) setEmailFrequency("none");
    else setEmailFrequency(FREQUENCY_TO_KEY[cfg.frequency] || "once");

    setReportsToInclude({
      travelTime: cfg.travelTime !== undefined ? cfg.travelTime : true,
      congestion: cfg.congestion !== undefined ? cfg.congestion : true,
      construction: cfg.construction !== undefined ? cfg.construction : true,
      incidents: cfg.incident !== undefined ? cfg.incident : true,
      weather: cfg.weather !== undefined ? cfg.weather : true,
    });
    setReceiveAllSevere(!!cfg.allSevereIncidents);

    if (cfg.speedThreshold !== undefined && cfg.speedThreshold < 999) {
      setDisableSpeedThreshold(false);
      setSpeedThreshold(mpsToMph(cfg.speedThreshold));
    } else {
      setDisableSpeedThreshold(cfg.speedThreshold !== undefined);
      setSpeedThreshold(35);
    }

    setDaysOfWeek(
      cfg.daysOfWeek || {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
      },
    );

    const period = parseTimeOfDayRange(cfg.period);
    setStartTime(period?.start || "07:00");
    setEndTime(period?.end || "09:00");

    const quiet = parseTimeOfDayRange(cfg.noIncidentsPeriod);
    setEnableQuietHours(!!quiet);
    setQuietHoursStart(quiet?.start || "22:00");
    setQuietHoursEnd(quiet?.end || "06:00");

    // Clear send now state on open
    setSendNowMessage(null);
  }, [initialConfig, isOpen]);

  // Turn off severe incidents option when incidents report is disabled
  useEffect(() => {
    if (!reportsToInclude.incidents) {
      setReceiveAllSevere(false);
    }
  }, [reportsToInclude.incidents]);

  // Cleanup send now timer
  useEffect(() => {
    return () => {
      if (sendNowTimerRef.current) clearTimeout(sendNowTimerRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const isVerified = userData?.emailVerified !== false;

  const handleDayToggle = (day) => {
    setDaysOfWeek((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleReportToggle = (report) => {
    setReportsToInclude((prev) => ({
      ...prev,
      [report]: !prev[report],
    }));
  };

  const buildAlertConfig = () => ({
    userId: userData?.id,
    routeData,
    schedule: {
      startTime,
      endTime,
      daysOfWeek,
    },
    preferences: {
      emailFrequency,
      reportsToInclude,
      speedThreshold: disableSpeedThreshold ? 1000 : speedThreshold,
      receiveAllSevere,
      quietHours:
        enableQuietHours && receiveAllSevere
          ? {
              enabled: true,
              start: quietHoursStart,
              end: quietHoursEnd,
            }
          : {
              enabled: false,
            },
    },
  });

  const handleSubmit = () => {
    onSaveConfig(buildAlertConfig());
  };

  const handleSendNow = async () => {
    if (!routeId || sendingNow) return;
    setSendingNow(true);
    setSendNowMessage(null);

    try {
      // Send the current (possibly unsaved) form state as a preferences override
      await sendNowEmail(routeId, toAlertPreferencesDto(buildAlertConfig()));
      setSendNowMessage({ type: "success", text: "Alert email sent!" });
    } catch (err) {
      setSendNowMessage({
        type: "error",
        text: err.message || "Failed to send email.",
      });
    } finally {
      setSendingNow(false);
      // Auto-clear message after 5 seconds
      if (sendNowTimerRef.current) clearTimeout(sendNowTimerRef.current);
      sendNowTimerRef.current = setTimeout(() => setSendNowMessage(null), 5000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Configure Route</CardTitle>
          <CardDescription>
            Customize when and how you receive traffic alerts for your route
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {/* Email verification warning */}
          {!isVerified && (
            <Alert className="border-yellow-300 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-yellow-800">
                Email not verified
              </AlertTitle>
              <AlertDescription className="text-yellow-700">
                Your email address is not verified. Scheduled alerts and Send
                Now are disabled.{" "}
                <a
                  href="/Trips/MyAccount"
                  className="underline font-medium text-yellow-800"
                >
                  Verify in My Account
                </a>
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* All settings disabled when not verified */}
          <div
            className={
              !isVerified ? "opacity-50 pointer-events-none" : undefined
            }
          >
            {/* Email Frequency Section - at top */}
            <div className="space-y-2">
              <h3 className="text-md font-medium">Email Frequency</h3>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => setEmailFrequency("none")}
                  className={`py-2 px-3 rounded-md text-sm ${
                    emailFrequency === "none"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  No emails
                </button>
                <button
                  type="button"
                  onClick={() => setEmailFrequency("once")}
                  className={`py-2 px-3 rounded-md text-sm ${
                    emailFrequency === "once"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  Once per period
                </button>
                <button
                  type="button"
                  onClick={() => setEmailFrequency("15min")}
                  className={`py-2 px-3 rounded-md text-sm ${
                    emailFrequency === "15min"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  Every 15 minutes
                </button>
                <button
                  type="button"
                  onClick={() => setEmailFrequency("30min")}
                  className={`py-2 px-3 rounded-md text-sm ${
                    emailFrequency === "30min"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  Every 30 minutes
                </button>
                <button
                  type="button"
                  onClick={() => setEmailFrequency("60min")}
                  className={`py-2 px-3 rounded-md text-sm ${
                    emailFrequency === "60min"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  Every hour
                </button>
              </div>
            </div>

            {/* Remaining sections disabled when "No emails" */}
            <div
              className={`space-y-4 mt-4 ${emailFrequency === "none" ? "opacity-50 pointer-events-none" : ""}`}
            >
              {/* Time Window Section */}
              <div className="space-y-2">
                <h3 className="text-md font-medium">Email Time Window</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TimeSelector
                    value={startTime}
                    onChange={setStartTime}
                    label="Start Time"
                  />
                  <TimeSelector
                    value={endTime}
                    onChange={setEndTime}
                    label="End Time"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  You'll receive emails for traffic conditions during this time
                  window
                </p>
              </div>

              {/* Days of Week Section */}
              <div className="space-y-2">
                <h3 className="text-md font-medium">Email Days</h3>
                <div className="grid grid-cols-7 gap-1">
                  {[
                    { key: "monday", label: "Monday" },
                    { key: "tuesday", label: "Tuesday" },
                    { key: "wednesday", label: "Wednesday" },
                    { key: "thursday", label: "Thursday" },
                    { key: "friday", label: "Friday" },
                    { key: "saturday", label: "Saturday" },
                    { key: "sunday", label: "Sunday" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleDayToggle(key)}
                      className={`py-1 px-1 rounded-md text-sm text-center ${
                        daysOfWeek[key]
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  You'll receive emails on these days of the week
                </p>
              </div>

              {/* Reports to Include Section */}
              <div className="space-y-2">
                <h3 className="text-md font-medium">Reports to Include</h3>

                {/* First row: Travel Time, Congestion, Construction */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => handleReportToggle("travelTime")}
                    className={`py-2 px-3 rounded-md text-sm ${
                      reportsToInclude.travelTime
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Travel Time
                  </button>

                  <button
                    type="button"
                    onClick={() => handleReportToggle("congestion")}
                    className={`py-2 px-3 rounded-md text-sm ${
                      reportsToInclude.congestion
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Congestion
                  </button>

                  <button
                    type="button"
                    onClick={() => handleReportToggle("construction")}
                    className={`py-2 px-3 rounded-md text-sm ${
                      reportsToInclude.construction
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Construction
                  </button>
                </div>

                {/* Second row: Incidents, Weather Alerts */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleReportToggle("incidents")}
                    className={`py-2 px-3 rounded-md text-sm ${
                      reportsToInclude.incidents
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Incidents
                  </button>

                  <button
                    type="button"
                    onClick={() => handleReportToggle("weather")}
                    className={`py-2 px-3 rounded-md text-sm ${
                      reportsToInclude.weather
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Weather Alerts
                  </button>
                </div>

                {/* Conditional row: Receive all severe incidents */}
                {reportsToInclude.incidents && (
                  <div className="ml-6 space-y-3">
                    <label className="flex items-center space-x-2 cursor-pointer py-1">
                      <input
                        type="checkbox"
                        checked={receiveAllSevere}
                        onChange={() => setReceiveAllSevere(!receiveAllSevere)}
                        className="mr-2"
                      />
                      <span>
                        Receive emails for all severe incidents on this route,
                        regardless of time
                      </span>
                    </label>

                    {/* Quiet Hours - only visible when Receive All Severe is enabled */}
                    {receiveAllSevere && (
                      <div className="ml-6 p-3 bg-gray-50 rounded-md space-y-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={enableQuietHours}
                            onChange={() =>
                              setEnableQuietHours(!enableQuietHours)
                            }
                            className="mr-2"
                          />
                          <span>Set quiet hours</span>
                        </label>

                        {/* Quiet Hours Time Selection */}
                        {enableQuietHours && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                            <TimeSelector
                              value={quietHoursStart}
                              onChange={setQuietHoursStart}
                              label="Quiet Start"
                            />
                            <TimeSelector
                              value={quietHoursEnd}
                              onChange={setQuietHoursEnd}
                              label="Quiet End"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Speed Threshold Section */}
              <div className="space-y-2">
                <h3 className="text-md font-medium">Speed Threshold</h3>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-gray-700">
                      Alert when traffic speed falls below:
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={disableSpeedThreshold}
                        onChange={() =>
                          setDisableSpeedThreshold(!disableSpeedThreshold)
                        }
                        className="mr-2"
                      />
                      <span className="text-sm">Disable threshold</span>
                    </label>
                  </div>

                  <div
                    className={`flex items-center ${
                      disableSpeedThreshold ? "opacity-50" : ""
                    }`}
                  >
                    <input
                      type="range"
                      min="5"
                      max="70"
                      step="5"
                      value={speedThreshold}
                      onChange={(e) =>
                        setSpeedThreshold(Number(e.target.value))
                      }
                      className="w-full mr-4"
                      disabled={disableSpeedThreshold}
                    />
                    <span className="text-lg font-medium w-12 text-center">
                      {disableSpeedThreshold ? "Off" : `${speedThreshold}`}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
                    <span>5 mph</span>
                    <span>70 mph</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex justify-between">
          <div>
            {/* Send Now button — only for existing saved routes */}
            {routeId && isVerified && (
              <Button
                variant="outline"
                type="button"
                onClick={handleSendNow}
                disabled={sendingNow || loading}
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                {sendingNow ? "Sending..." : "Send Now"}
              </Button>
            )}
            {sendNowMessage && (
              <span
                className={`ml-3 text-sm ${
                  sendNowMessage.type === "success"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {sendNowMessage.text}
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              type="button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !isVerified}
            >
              {loading ? "Saving..." : saveLabel || "Save Configuration"}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RouteEmailConfig;
